import type { EmberDoneEvent, EmberErrorEvent, EmberStreamingEvent } from '@/src/api/types/generated-types'
import { useSocketIoListener } from '@/src/hooks/useSocketIoListener.hook'
import { useTranslation } from 'react-i18next'
import { useEmberMessagesStore } from '../ember-messages.store'

export type MessageHandlingCustomActions<D extends EmberDoneEvent> = {
  onStreaming?: (event: EmberStreamingEvent) => void
  onDone?: (event: D) => void
  onError?: (event: EmberErrorEvent) => void
}

export const useEmberBaseMessageHandling = <D extends EmberDoneEvent>(
  threadId: string,
  customActions: MessageHandlingCustomActions<D> = {}
) => {
  const { t } = useTranslation()

  const handleStreaming = (event: EmberStreamingEvent) => {
    if (event.eventName !== 'streaming' || !event.textDelta || event.threadId !== threadId) return
    useEmberMessagesStore.getState().handleStreamingUpdate(event.threadId, event.textDelta)
    customActions.onStreaming?.(event)
  }

  const handleErrorEvent = (event: EmberErrorEvent) => {
    const { eventName, errorMessage } = event
    if (eventName !== 'error' || event.threadId !== threadId) return
    if (errorMessage === 'Aborted') {
      useEmberMessagesStore.getState().handleErrorMessage(event.threadId, t('emberChat.cancelled'))
    } else {
      useEmberMessagesStore.getState().handleErrorMessage(event.threadId, t('emberChat.failed'))
    }
    customActions.onError?.(event)
  }

  const handleDone = async (event: D) => {
    if (event.eventName !== 'done' || event.threadId !== threadId) return
    useEmberMessagesStore.getState().completeLastMessage(event.threadId)
    customActions.onDone?.(event)
  }

  useSocketIoListener('emberChat.streaming', handleStreaming)
  useSocketIoListener('emberChat.error', handleErrorEvent)
  useSocketIoListener('emberChat.done', handleDone)
}
